import dayjs from "dayjs";
import { CoinsIcon, ImportIcon, PackageOpenIcon, PlusIcon, SortAscIcon, Trash2Icon } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { Link, useParams } from "react-router";
import { LoadingState } from "@/ds/custom/LoadingState";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { StatePanel } from "@/ds/custom/StatePanel";
import { Statistic, StatisticsBar } from "@/ds/custom/StatisticsBar";
import { Button } from "@/ds/shadcn/components/ui/button";
import { DropdownMenuGroup, DropdownMenuItem } from "@/ds/shadcn/components/ui/dropdown-menu";
import type { Money } from "@/lib/domain/Money";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { useNotifyMutationResult } from "@/lib/common/useNotifyMutationResult";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { SpreadSheet, SpreadSheetCell, SpreadSheetEditableDateCell, SpreadSheetEditableNumberCell, SpreadSheetEditableOptionCell, SpreadSheetRow } from "@/features/common/components/SpreadSheet";
import { ListItemMenu } from "@/features/common/components/dataview/ListItem";
import { HeaderBarSelector } from "@/features/common/components/HeaderBarSelector";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { ModalCurrency } from "@/features/common/components/ModalCurrency";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { SearchBar } from "@/features/common/components/SearchBar";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { VariantSelector } from "@/features/common/components/VariantSelector";
import { usePreferredCurrency } from "@/features/common/api/usePreferredCurrency";
import { TransactionActionOptions } from "@/features/collections/TransactionAction";
import { TransactionDetailsSortOrderOptions } from "@/features/collections/TransactionDetailsSortOrder";
import type { CollectionVariantDetailsModel } from "@/features/collections/api/models/CollectionVariantDetailsModel";
import type { TransactionDetailsModel } from "@/features/collections/api/models/TransactionDetailsModel";
import { useCollectionVariantDetails } from "@/features/collections/api/useCollectionVariantDetails";
import { useTransactionCreate } from "@/features/collections/api/useTransactionCreate";
import { useTransactionDelete } from "@/features/collections/api/useTransactionDelete";
import { useTransactionDetails } from "@/features/collections/api/useTransactionDetails";
import { useTransactionUpdate } from "@/features/collections/api/useTransactionUpdate";
import { useVariantTransactionDetails } from "@/features/collections/api/useVariantTransactionDetails";
import type { GetProductAndVariantsMarketPriceResponseModel, VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";

const HEADERS = ["Product", "Quantity", "Action", "Unit Amount", "Total", "Date", ""];
const HEADER_CLASS_NAMES = ["", "w-20", "w-22", "w-34", "w-38", "w-28", "w-13"];

interface TransactionToDelete {
	transactionId: string;
	variantId: string;
}

interface TransactionToChangeCurrency {
	transactionId: string;
	variantId: string;
	unitAmount: Money;
}

interface TransactionRowAdditionalProps {
	collectionId: string;
	isVariantSpecificView: boolean;
	onDelete: (details: TransactionToDelete) => void;
	onChangeCurrency: (details: TransactionToChangeCurrency) => void;
}

export const TabCollectionEdit: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;
	const variantId = params.variantId;

	const { state, dispatchSetTransactionDetailsSortOrder } = useUserPreferencesState();
	const preferredCurrency = usePreferredCurrency();
	const [searchQuery, setSearchQuery] = useState("");
	const collectionTransactionDetails = useTransactionDetails(collectionId, state.transactionDetailsSortOrder, searchQuery, !variantId);
	const variantTransactionDetails = useVariantTransactionDetails(collectionId, variantId);
	const transactionDetails = variantId ? variantTransactionDetails : collectionTransactionDetails;
	const collectionVariantDetails = useCollectionVariantDetails(collectionId, variantId);

	const createTransaction = useTransactionCreate();
	useNotifyMutationResult(createTransaction, "Transaction Created", "Your transaction was created successfully.");

	const deleteTransaction = useTransactionDelete();
	useNotifyMutationSuccess(deleteTransaction, "Transaction Deleted", "Your transaction was deleted successfully.");

	const updateTransaction = useTransactionUpdate();

	const [variantSelectorVisible, setVariantSelectorVisible] = useState(false);
	const [transactionToDelete, setTransactionToDelete] = useState<TransactionToDelete | null>(null);
	const [transactionToChangeCurrency, setTransactionToChangeCurrency] = useState<TransactionToChangeCurrency | null>(null);

	const ref = useRef<HTMLDivElement>(null);
	const leftPanelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!deleteTransaction.isSuccess) {
			return;
		}
		setTransactionToDelete(null);
	}, [deleteTransaction.isSuccess]);

	useEffect(() => {
		if (!updateTransaction.isSuccess) {
			return;
		}
		setTransactionToChangeCurrency(null);
	}, [updateTransaction.isSuccess]);

	const onVariantSelected = useCallback((_product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => {
		const unitAmount: Money = variant.marketPrice || {
			currency: preferredCurrency.data?.currency || "USD",
			value: 0
		};

		createTransaction.mutate({
			requestModel: {
				variantId: variant.variantId,
				occurredDate: dayjs().format("YYYY-MM-DD"),
				action: "Purchase",
				quantity: 1,
				unitAmount
			},
			collectionId
		});
	}, [createTransaction, preferredCurrency.data, collectionId]);

	const onCreateClick = useCallback(() => {
		if (variantId && collectionVariantDetails.data) {
			createTransaction.mutate({
				requestModel: {
					variantId,
					occurredDate: dayjs().format("YYYY-MM-DD"),
					action: "Purchase",
					quantity: 1,
					unitAmount: {
						value: collectionVariantDetails.data.marketValue.value / collectionVariantDetails.data.quantity,
						currency: collectionVariantDetails.data.marketValue.currency
					}
				},
				collectionId
			});
			return;
		}

		setVariantSelectorVisible(true);
	}, [variantId, collectionVariantDetails.data, createTransaction, collectionId]);

	const createDisabled = variantSelectorVisible || createTransaction.isPending || (!!variantId && !collectionVariantDetails.data);

	return (
		<>
			<HeaderBarTools>
				{
					!variantId && (
						<HeaderBarSelector
							value={state.transactionDetailsSortOrder}
							onChange={dispatchSetTransactionDetailsSortOrder}
							placeholder="Sort"
							icon={SortAscIcon}
							options={TransactionDetailsSortOrderOptions} />
					)
				}

				<Link to={`/collections/${collectionId}/transactions/csv-import`}>
					<Button variant="outline" className="w-full">
						<ImportIcon /> Import
					</Button>
				</Link>

				<Button
					variant="outline"
					onClick={onCreateClick}
					disabled={createDisabled}>
					<PlusIcon /> Create
				</Button>
			</HeaderBarTools>

			{
				variantId && collectionVariantDetails.data && (
					<>
						<CollectionVariantHeader collectionVariantDetails={collectionVariantDetails.data} />
						<CollectionVariantStatisticsBar collectionVariantDetails={collectionVariantDetails.data} />
					</>
				)
			}

			<ErrorDisplay error={transactionDetails.error} />

			{!variantId && <SearchBar value={searchQuery} onChange={setSearchQuery} />}

			<SplitScreen
				fixed
				visible={variantSelectorVisible || (createTransaction.isPending && !variantId)}
				onVisibleChange={setVariantSelectorVisible}>
				<SplitScreenLeftPanel ref={leftPanelRef}>
					<SpreadSheet
						forQuery={transactionDetails}
						getKey={x => x.transactionId}
						headers={HEADERS}
						headerClassNames={HEADER_CLASS_NAMES}
						rowComponent={TransactionSpreadSheetRow}
						additionalProps={{ collectionId, isVariantSpecificView: !!variantId, onDelete: setTransactionToDelete, onChangeCurrency: setTransactionToChangeCurrency }}
						scrollRef={leftPanelRef}>

						<StatePanel
							icon={PackageOpenIcon}
							title="No Transactions Yet"
							description="Create a transaction to start tracking your collection.">
							<Button
								variant="outline"
								disabled={createDisabled}
								onClick={onCreateClick}>
								Create Transaction
							</Button>
						</StatePanel>
					</SpreadSheet>
				</SplitScreenLeftPanel>
				<SplitScreenRightPanel ref={ref}>
					{
						createTransaction.isPending ? (
							<LoadingState />
						) : (
							<VariantSelector onSelect={onVariantSelected} onCancel={() => setVariantSelectorVisible(false)} scrollRef={ref} />
						)
					}
				</SplitScreenRightPanel>
			</SplitScreen>

			<ModalConfirm
				title="Delete Transaction"
				description="Are you sure you want to delete this transaction?"
				confirmText="Delete"
				confirmDestructive
				isLoading={deleteTransaction.isPending}
				error={deleteTransaction.error}
				open={!!transactionToDelete}
				onOpenChange={open => {
					if (!open) {
						setTransactionToDelete(null);
						deleteTransaction.reset();
					}
				}}
				onConfirm={() => transactionToDelete && deleteTransaction.mutate({ ...transactionToDelete, collectionId })} />

			<ModalCurrency
				open={!!transactionToChangeCurrency}
				onOpenChange={open => !open && setTransactionToChangeCurrency(null)}
				value={transactionToChangeCurrency?.unitAmount.currency ?? "USD"}
				onConfirm={currency => transactionToChangeCurrency && updateTransaction.mutate({
					requestModel: {
						occurredDate: null,
						action: null,
						quantity: null,
						unitAmount: { value: transactionToChangeCurrency.unitAmount.value, currency }
					},
					transactionId: transactionToChangeCurrency.transactionId,
					collectionId,
					variantId: transactionToChangeCurrency.variantId
				})}
				isLoading={updateTransaction.isPending}
				error={updateTransaction.error} />
		</>
	);
};

interface CollectionVariantHeaderProps {
	collectionVariantDetails: CollectionVariantDetailsModel;
}

const CollectionVariantHeader: FC<CollectionVariantHeaderProps> = props => {
	const { collectionVariantDetails } = props;

	return (
		<div className="p-4 border-b">
			<SmallProductDisplay
				productName={collectionVariantDetails.productName}
				fcId={collectionVariantDetails.fcId}
				imageUrl={collectionVariantDetails.imageUrl}
				className="h-24 w-auto" />
		</div>
	);
};

interface CollectionVariantStatisticsBarProps {
	collectionVariantDetails: CollectionVariantDetailsModel;
}

const CollectionVariantStatisticsBar: FC<CollectionVariantStatisticsBarProps> = props => {
	const { collectionVariantDetails } = props;

	return (
		<StatisticsBar>
			<Statistic title="Market Value">
				<MoneyDisplay mode="Standard" money={collectionVariantDetails.marketValue} />
			</Statistic>
			<Statistic title="Total Quantity">
				{collectionVariantDetails.quantity}
			</Statistic>
			<Statistic title="Average Buy Price">
				<MoneyDisplay mode="Standard" money={collectionVariantDetails.averageBuyPrice} />
			</Statistic>
			<Statistic title="Gain">
				<MoneyDisplay mode="DeltaPercentage" money={collectionVariantDetails.gain} originalValue={collectionVariantDetails.invested.value} />
			</Statistic>
			<Statistic title="Invested">
				<MoneyDisplay mode="Standard" money={collectionVariantDetails.invested} />
			</Statistic>
			<Statistic title="Profit">
				<MoneyDisplay mode="Delta" money={collectionVariantDetails.profit} />
			</Statistic>
			<Statistic title="Cash Position">
				<MoneyDisplay mode="Delta" money={collectionVariantDetails.cashPosition} />
			</Statistic>
		</StatisticsBar>
	);
};

interface TransactionSpreadSheetRowProps extends TransactionDetailsModel, TransactionRowAdditionalProps {}

const TransactionSpreadSheetRow: FC<TransactionSpreadSheetRowProps> = props => {
	const {
		transactionId,
		variantId,
		productId,
		productName,
		fcId,
		imageUrl,
		occurred,
		quantity,
		action,
		unitAmount,
		collectionId,
		isVariantSpecificView,
		onDelete,
		onChangeCurrency
	} = props;

	const updateTransaction = useTransactionUpdate();

	const total: Money = { value: quantity * unitAmount.value, currency: unitAmount.currency };
	const formattedDate = dayjs(occurred).format("DD MMM YYYY");

	const productLink = isVariantSpecificView ? `/browse/product/${productId}` : `/collections/${collectionId}/transactions/${variantId}`;

	return (
		<SpreadSheetRow error={updateTransaction.error}>
			<SpreadSheetCell className="hover:bg-background-highlight transition-colors duration-150">
				<Link to={productLink} className="block min-w-0">
					<SmallProductDisplay productName={productName} fcId={fcId} imageUrl={imageUrl} className="w-5" />
				</Link>
			</SpreadSheetCell>
			<SpreadSheetEditableNumberCell
				value={quantity}
				isLoading={updateTransaction.isPending}
				isSuccess={updateTransaction.isSuccess}
				inputMode="numeric"
				parse={draft => {
					const n = parseInt(draft, 10);
					return isNaN(n) || n < 1 ? null : n;
				}}
				onCommit={value => updateTransaction.mutate({
					requestModel: { occurredDate: null, action: null, quantity: value, unitAmount: null },
					transactionId,
					collectionId,
					variantId
				})}>
				{quantity}
			</SpreadSheetEditableNumberCell>
			<SpreadSheetEditableOptionCell
				value={action}
				isLoading={updateTransaction.isPending}
				options={TransactionActionOptions}
				onCommit={value => updateTransaction.mutate({
					requestModel: { occurredDate: null, action: value, quantity: null, unitAmount: null },
					transactionId,
					collectionId,
					variantId
				})}>
				{action}
			</SpreadSheetEditableOptionCell>
			<SpreadSheetEditableNumberCell
				value={unitAmount.value}
				isLoading={updateTransaction.isPending}
				isSuccess={updateTransaction.isSuccess}
				onCommit={value => updateTransaction.mutate({
					requestModel: {
						occurredDate: null,
						action: null,
						quantity: null,
						unitAmount: { value, currency: unitAmount.currency }
					},
					transactionId,
					collectionId,
					variantId
				})}>
				<MoneyDisplay mode="Standard" money={unitAmount} />
			</SpreadSheetEditableNumberCell>
			<SpreadSheetCell>
				<MoneyDisplay mode="Standard" money={total} />
			</SpreadSheetCell>
			<SpreadSheetEditableDateCell
				value={occurred}
				isLoading={updateTransaction.isPending}
				isSuccess={updateTransaction.isSuccess}
				onCommit={date => updateTransaction.mutate({
					requestModel: { occurredDate: date, action: null, quantity: null, unitAmount: null },
					transactionId,
					collectionId,
					variantId
				})}>
				{formattedDate}
			</SpreadSheetEditableDateCell>
			<SpreadSheetCell className="px-0">
				<div className="flex justify-center">
					<ListItemMenu>
						<DropdownMenuGroup>
							<DropdownMenuItem onClick={() => onChangeCurrency({ transactionId, variantId, unitAmount })}>
								<CoinsIcon />
								Change Currency
							</DropdownMenuItem>
							<DropdownMenuItem variant="destructive" onClick={() => onDelete({ transactionId, variantId })}>
								<Trash2Icon />
								Delete
							</DropdownMenuItem>
						</DropdownMenuGroup>
					</ListItemMenu>
				</div>
			</SpreadSheetCell>
		</SpreadSheetRow>
	);
};
